import * as Yup from 'yup';
import { DealerManagementService } from 'src/services/dealer-management.service';
import FooterPrivacy from 'src/views/auth/components/baseTemplate/FooterPrivacy';
import { getBrandByDealerNumber } from 'src/utils/helpers';
import Header from 'src/views/pages/system-admin/dealer-group-permissions/components/header';
import Input from 'src/components/forms/input';
import { IUser } from 'src/utils/interfaces/user';
import { MODAL_CONTROL_ACTIONS } from 'src/utils/enums/user-store-actions.enum';
import Spinner from 'src/components/ui/spinner';
import { useFormik } from 'formik';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import './styles.scss';

const validationSchema = Yup.object({
    dealerId: Yup.string()
        .trim()
        .required('This field is required')
        .matches(/^[A-Za-z0-9]{6}$/, 'Missing or incomplete search criteria.'),
});

const DealerGroupAdd = () => {
    const [showMessage, setShowMessage] = useState({
        show: false,
        success: false,
    });
    const { state } = useLocation();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const dispatch = useDispatch();

    if (!state?.dealerId) {
        return (
            <Navigate
                to='/dashboard/systemadmin/dealer-group-maintenance/search'
                replace
            />
        );
    }

    const formik = useFormik({
        initialValues: {
            dealerId: '',
        },
        validationSchema,
        validateOnChange: false,
        onSubmit: onsubmit,
    });
    const { values, errors, touched, handleSubmit, setFieldValue, handleBlur } =
        formik;

    const handleActiveModal = () => {
        dispatch({
            type: MODAL_CONTROL_ACTIONS.SET_ACTIVE,
            payload: {
                titleContent: 'Are you sure you want to leave?',
                bodyContent:
                    'Any information you’ve entered will not be saved.',
                cancelBtnLabel: 'Cancel',
                confirmBtnLabel: 'Yes, confirm',
                onCancelHandler: cancelhandler,
                onConfirmHandler: confirmHandler,
            },
        });
    };

    useEffect(() => {
        handleActiveModal();
    }, []);

    const headerBackClickHandler = () => {
        dispatch({
            type: MODAL_CONTROL_ACTIONS.SET_SHOW,
            payload: {
                shouldShow: true,
                onCancelHandler: cancelhandler,
                onConfirmHandler: confirmHandler,
            },
        });
    };

    const cancelhandler = () => {
        dispatch({
            type: MODAL_CONTROL_ACTIONS.SET_SHOW,
            payload: { shouldShow: false },
        });
    };

    const confirmHandler = () => {
        dispatch({
            type: MODAL_CONTROL_ACTIONS.SET_INACTIVE,
            payload: { shouldShow: false },
        });
        setTimeout(() => {
            navigate('/dashboard/systemadmin/dealer-group-maintenance/');
        }, Number('700'));
    };

    let brand = 'vw';
    const currentDealers = useSelector((state: IUser) => {
        return state.currentDealers;
    });
    const dealerDataInfo = useSelector((state: IUser) => {
        return state.dealerDataInfo;
    });
    if (currentDealers && dealerDataInfo) {
        const { dealer } = dealerDataInfo;
        brand = getBrandByDealerNumber(dealer.brand, dealer.dealerNumber);
    }

    async function onsubmit() {
        setShowMessage((values) => {
            return { ...values, show: false, success: false };
        });
        setIsLoading(true);
        const service = new DealerManagementService();
        try {
            await service.addDealerToGroup(
                values.dealerId,
                state.dealerGroupId,
            );
            setShowMessage((values) => {
                return { ...values, show: true, success: true };
            });
        } catch (error) {
            setShowMessage((values) => {
                return { ...values, show: true, success: false };
            });
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <>
            <Header backClickHandler={headerBackClickHandler} />
            <div className='dashboard-content-wrapper'>
                <div className='dealer-group-maintenance dashboard-content-container'>
                    <section className='o-content-section'>
                        {showMessage.show ? (
                            <div className={''}>
                                <Notification
                                    content={
                                        showMessage.success
                                            ? 'Dealer successfully added.'
                                            : 'The Dealer Number cannot be added, as it was not found in dSource or is already associated with a Dealer Group.'
                                    }
                                    type={
                                        showMessage.success
                                            ? NotificationType.SUCCESS
                                            : NotificationType.ERROR
                                    }
                                    visibility={showMessage.show}
                                    setVisibility={() => {
                                        setShowMessage((values) => {
                                            return {
                                                ...values,
                                                show: false,
                                                success: false,
                                            };
                                        });
                                    }}
                                />{' '}
                            </div>
                        ) : null}
                        <div className='page-title'>
                            <h5>Add Dealer Number</h5>
                        </div>
                        <hr></hr>
                        <section className='c-form-section'>
                            <div className='c-form-section__content'>
                                <div className='o-fieldset__row u-mt-x-small u-mb-x-small'>
                                    <div className='o-layout__item u-1/1 u-1/1@xs'>
                                        <p>
                                            Enter a valid Dealer Number below.
                                            Click the Add dealer button to add a
                                            dealer to this group.
                                        </p>
                                    </div>
                                    <form
                                        className='o-layout__item u-1/1 u-1/1@xs'
                                        onSubmit={handleSubmit}
                                    >
                                        <div className='o-layout__item u-1/1 u-1/1@xs'>
                                            <div className='c-form-field'>
                                                <div className='c-form-field__box'>
                                                    <Input
                                                        labelName={
                                                            'Dealer Number *'
                                                        }
                                                        type={'text'}
                                                        name={'dealerId'}
                                                        maxlength={6}
                                                        handlerChange={(
                                                            e: React.ChangeEvent<HTMLInputElement>,
                                                        ) => {
                                                            setFieldValue(
                                                                'dealerId',
                                                                e.target.value.toUpperCase(),
                                                            );
                                                        }}
                                                        handlerBlur={handleBlur}
                                                        value={values.dealerId}
                                                        asErrors={
                                                            errors.dealerId
                                                        }
                                                        asTouched={
                                                            touched.dealerId
                                                        }
                                                        asSubmitError={
                                                            errors.dealerId
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className='o-layout__item u-1/1 u-1/1@xs'>
                                            <div className='o-fieldset__row button-group'>
                                                <button
                                                    className={
                                                        'c-btn c-btn--secondary'
                                                    }
                                                    type='button'
                                                    disabled={isLoading}
                                                    onClick={
                                                        headerBackClickHandler
                                                    }
                                                >
                                                    <span className='c-btn__text'>
                                                        Cancel
                                                    </span>
                                                </button>
                                                <button
                                                    className={`c-btn btn--secondary ${brand}`}
                                                    type='submit'
                                                    disabled={
                                                        isLoading ||
                                                        values.dealerId.length <
                                                            Number('6')
                                                    }
                                                    onClick={() => {
                                                        handleSubmit();
                                                    }}
                                                >
                                                    <span className='c-btn__text'>
                                                        {isLoading ? (
                                                            <Spinner />
                                                        ) : (
                                                            'Add dealer'
                                                        )}
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </section>
                        <div style={{ gridColumnStart: 2, marginTop: '40px' }}>
                            <FooterPrivacy />
                        </div>
                    </section>
                </div>
            </div>
        </>
    );
};

export default DealerGroupAdd;
